import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { api } from '../lib/api';

type Item = { to: string; label: string; icon: string; adminOnly?: boolean; systemOnly?: boolean;
  // Only meaningful on a state/grantor deployment; hidden entirely on an ISP box.
  grantorOnly?: boolean;
  // Grantor navigation is an ALLOWLIST, not a denylist: a state box shows only
  // items explicitly marked grantorKeep (or grantorOnly). That way every future ISP
  // feature - crews, purchase orders, payroll - stays out of a state's product by
  // default instead of leaking in until someone remembers to hide it.
  grantorKeep?: boolean };
type Section = {
  label: string;
  items: Item[];
  // Sections marked alwaysOpen don't collapse and don't show a chevron.
  alwaysOpen?: boolean;
};

// Lifecycle-driven IA. The menu now reads as the owner's actual journey
// through a BEAD award, in order:
//   Set Up (the award)  ->  Design  ->  Build  ->  Spend  ->  Compliance
//   ->  Report & Get Paid.
// Each numbered section is one phase of that lifecycle; Home and Settings
// are un-numbered because they sit outside the sequence.
const sections: Section[] = [
  {
    label: 'Home',
    alwaysOpen: true,
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: '\u{1F3E0}', grantorKeep: true },
    ],
  },
  {
    // The state's own desk. Both entries are grantorOnly, so on an ISP box this whole
    // section filters down to nothing and disappears.
    label: 'Oversight',
    alwaysOpen: true,
    items: [
      { to: '/grantor/intake', label: 'Request Intake',   icon: '\u{1F4E5}', grantorOnly: true },
      { to: '/grantor',        label: 'Oversight Queue', icon: '\u{1F5C2}', grantorOnly: true },
      { to: '/grantor/reassignment', label: 'Reassignment', icon: '\u{1F501}', grantorOnly: true },
      { to: '/grantor/ledger', label: 'Decision Ledger', icon: '\u{1F4D2}', grantorOnly: true },
    ],
  },
  {
    label: '1 · Set Up',
    items: [
      { to: '/compliance/awards',      label: 'Awards',            icon: '\u{1F3C6}', grantorKeep: true },
      { to: '/compliance/obligations', label: 'Obligation Catalog', icon: '\u{1F4CB}' },
      { to: '/compliance/grants',      label: 'Grant Assignments', icon: '\u{1F517}' },
      { to: '/budgets',                label: 'Budget',            icon: '\u{1F4B5}' },
    ],
  },
  {
    label: '2 · Design',
    items: [
      { to: '/builder',           label: 'Builder',           icon: '\u{1F5FA}️' },
      { to: '/grantor/builder', label: 'Builder',            icon: '\u{1F5FA}\uFE0F', grantorOnly: true },
      { to: '/descope',           label: 'De-Scope Review',   icon: '\u2696\uFE0F', grantorOnly: true },
      { to: '/project-economics', label: 'Project Economics', icon: '\u{1F4C8}', grantorKeep: true },
      { to: '/compliance/makeready', label: 'Make-Ready Risk', icon: '⚠️', grantorKeep: true },
      { to: '/plans-of-record',   label: 'Plans of Record',   icon: '\u{1F4D0}', grantorKeep: true },
    ],
  },
  {
    label: '3 · Build',
    items: [
      { to: '/crews',        label: 'Crews',           icon: '\u{1F477}' },
      { to: '/work-orders',  label: 'Work Orders',     icon: '\u{1F3AB}' },
      { to: '/construction', label: 'Construction',    icon: '\u{1F3D7}️' },
      { to: '/production',   label: 'Field Production', icon: '\u{1F4E6}' },
      { to: '/as-built',     label: 'As-built',        icon: '✅' },
      { to: '/permits',      label: 'Permits',         icon: '\u{1F4DC}' },
      { to: '/bead-compliance/ehp', label: 'Environmental', icon: '\u{1F332}' },
    ],
  },
  {
    label: '4 · Spend',
    items: [
      { to: '/procurement',     label: 'Procurement',     icon: '\u{1F6D2}' },
      { to: '/purchase-orders', label: 'Purchase Orders', icon: '\u{1F9FE}' },
      { to: '/invoices',        label: 'Invoices',        icon: '\u{1F4B3}' },
      { to: '/vendors',         label: 'Vendors',         icon: '\u{1F3E2}' },
    ],
  },
  // 5 and 6 are the subgrantee's obligations - BABA, Davis-Bacon, performance
  // measures, and drawing federal money down. A state office receives those, it
  // does not file them, so nothing in either section is grantorKeep and both
  // disappear on a state box.
  {
    label: '5 · Compliance',
    items: [
      { to: '/compliance',                      label: 'Overview',            icon: '\u{1F6E1}️' },
      { to: '/bead-compliance/baba',            label: 'BABA Ledger',         icon: '\u{1F1FA}\u{1F1F8}' },
      { to: '/bead-compliance/davis-bacon',     label: 'Davis-Bacon',         icon: '⚖️' },
      { to: '/compliance/performance-measures', label: 'Performance Measures', icon: '\u{1F4CA}' },
    ],
  },
  {
    label: '6 · Report & Get Paid',
    items: [
      { to: '/report',          label: 'Reporting & Draws', icon: '\u{1F4E4}' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: '/settings/getting-started', label: 'Getting started',    icon: '\u{1F9ED}' },
      // grantorKeep: a state's de-scope determination is built on THIS rate card, and the
      // determination itself concedes the unit costs are not the subgrantee's own and may
      // reasonably be contested. An office that cannot open the numbers it is being asked
      // to defend cannot defend them. PricingPage has no plant coupling, so it works on a
      // grantor box where the ISP plant screens do not.
      { to: '/settings/pricing',      label: 'Pricing',            icon: '\u{1F4B2}', grantorKeep: true },
      { to: '/compliance/rules',      label: 'Compliance Rules',   icon: '\u{1F4CF}' },
      { to: '/settings/integrations', label: 'Integrations',       icon: '\u{1F50C}', grantorKeep: true },
      { to: '/settings/qbo-mapping',  label: 'Accounting Mapping', icon: '\u{1F9EE}' },
      { to: '/settings/users',        label: 'Users',              icon: '\u{1F464}', adminOnly: true, grantorKeep: true },
      { to: '/settings/deployment',   label: 'Deployment',         icon: '\u{1F680}', grantorKeep: true },
      { to: '/settings/system',       label: 'System Recovery',    icon: '\u{1F527}', systemOnly: true, grantorKeep: true },
      { to: '/settings/sample-data',  label: 'Sample Data',        icon: '\u{1F9EA}' },
      // NOT grantorKeep. /settings is the ISP plant-engineering console — splitter ratio,
      // aerial lashing, take rate — keyed to plant.id, and GET /api/plants does not exist
      // on a grantor box. A state analyst who clicked this landed in fourteen tabs of
      // someone else's job. The grantor-relevant settings above are allowlisted
      // individually; this one is not.
      { to: '/settings',              label: 'Settings',           icon: '⚙️', grantorKeep: true },
    ],
  },
];

// Bumped to v3 for the lifecycle IA re-org: users carrying expanded-state
// for the old section labels (Plan/Finance/Integrations) get a fresh
// all-collapsed start under the new section names.
const STORAGE_KEY = 'mb.sidebar.expanded.v3';

// Find which section contains a given route. Used to auto-expand the
// section the user is currently inside.
function sectionForPath(pathname: string): string | null {
  // Pick the longest matching item.to (so /settings/catalog beats /settings)
  let best: { sectionLabel: string; len: number } | null = null;
  for (const sec of sections) {
    for (const item of sec.items) {
      if (pathname === item.to || pathname.startsWith(item.to + '/')) {
        if (!best || item.to.length > best.len) {
          best = { sectionLabel: sec.label, len: item.to.length };
        }
      }
    }
  }
  return best?.sectionLabel ?? null;
}

function loadExpanded(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export default function Sidebar() {
  const location = useLocation();
  const activeSection = sectionForPath(location.pathname);

  // Initial state: pure localStorage. No auto-expand on the active route --
  // demo audiences want to see the full IA at a glance, not a partially-
  // expanded menu that changes shape per page. The blue dot on a collapsed
  // section still flags "active page is in here."
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => loadExpanded());

  // Pull the current user's role so we can hide admin-only nav entries from
  // non-admin viewers.
  const { data: me } = useQuery<{ role?: string; is_system?: boolean }>({
    queryKey: ['me'],
    queryFn: () => api.get('/auth/me'),
    staleTime: 60_000,
  });
  // What kind of tenant is this box? Set at provision time; an ISP box never shows
  // grantor navigation even though the code ships in every image.
  const { data: cfg } = useQuery<{ deployment_kind?: string }>({
    queryKey: ['deployment-config-kind'],
    queryFn: () => api.get('/bead/config'),
    staleTime: 5 * 60 * 1000,
  });
  const isGrantor = cfg?.deployment_kind === 'grantor';
  const isAdmin = me?.role === 'admin';
  const isSystem = !!me?.is_system;

  // Persist user toggles.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(expanded)); } catch {}
  }, [expanded]);

  // Product identity follows the deployment kind. index.html ships one static title
  // because the same image boots as either product; the box only learns which one it
  // is after /bead/config answers, so the tab is corrected here rather than baked in.
  // A state broadband office should never see a browser tab that says it is running
  // software sold to the ISPs it regulates.
  useEffect(() => {
    if (!cfg) return;
    document.title = isGrantor ? 'Milestone Grantor' : 'Milestone Build';
  }, [cfg, isGrantor]);

  const toggle = (label: string) =>
    setExpanded(prev => ({ ...prev, [label]: !prev[label] }));

  return (
    <aside className="sidebar">
      <div className="sidebar__brand">
        <span className="sidebar__brand-mark">M</span>
        <span>Milestone <b style={{ fontWeight: 800 }}>{isGrantor ? 'Grantor' : 'Build'}</b></span>
      </div>

      {sections.map((section) => {
        const visible = section.items
          .filter(item => !item.adminOnly || isAdmin)
          .filter(item => !item.systemOnly || isSystem)
          .filter(item => !item.grantorOnly || isGrantor)
          // On a grantor box, show ONLY the allowlisted items.
          .filter(item => !isGrantor || item.grantorKeep || item.grantorOnly);
        // A section whose every item is filtered out for this deployment or role
        // renders nothing at all. A bare section header with no links under it reads
        // as a broken menu - which is exactly what a state would see now that the
        // ISP-only screens are out of the grantor allowlist.
        if (!visible.length) return null;
        const isOpen = section.alwaysOpen || !!expanded[section.label];
        const isActiveSection = activeSection === section.label;
        return (
          <div key={section.label}>
            {section.alwaysOpen ? (
              <div className="sidebar__section-label">{section.label}</div>
            ) : (
              <div
                className="sidebar__section-label"
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onClick={() => toggle(section.label)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle(section.label);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <span>
                  {section.label}
                  {!isOpen && isActiveSection && (
                    <span
                      title="Active page is inside this section"
                      style={{
                        display: 'inline-block', width: 6, height: 6, borderRadius: 999,
                        background: '#60a5fa', marginLeft: 6, verticalAlign: 'middle',
                      }}
                    />
                  )}
                </span>
                <span style={{
                  fontSize: 10, opacity: 0.7,
                  transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s ease',
                  display: 'inline-block',
                }}>{'▸'}</span>
              </div>
            )}

            {isOpen && (
              <nav className="sidebar__nav" style={{ marginBottom: 12 }}>
                {visible.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    /* Parent routes that own children must match exactly, or two links
                       light up at once (/grantor would stay active on /grantor/intake). */
                    end={item.to === '/settings' || item.to === '/grantor'}
                    className={({ isActive }) => clsx('sidebar__link', isActive && 'active')}
                  >
                    <span className="sidebar__icon" aria-hidden>{item.icon}</span>
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </nav>
            )}
          </div>
        );
      })}

      <div className="sidebar__footer">
        v0.1.0 {'·'} build {new Date().getFullYear()}
      </div>
    </aside>
  );
}

